import {
	Button,
	CircularProgress,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import {
	Archive,
	CircleArrowDown,
	Delete,
	Share2,
	SquarePen,
	Trash2,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { DocumentKeys } from "web_myplann/Api/Document/DocumentKeys";
import { useDocumentDeleteMutation } from "web_myplann/Api/Document/useDocumentDeleteMutation";
import { useDocumentEditMutation } from "web_myplann/Api/Document/useDocumentEditMutation";
import { useSaveDocumentMutation } from "web_myplann/Api/Document/useSaveDocumentMutation";
import { useShareDocumentMutation } from "web_myplann/Api/Document/useShareDocumentMutation";
import {
	type DocumentDTO,
	DocumentLocalTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DeleteDocumentDialog } from "web_myplann/Portfolio/Components/Document/DeleteDocumentDialog";
import { MoveDocumentDialog } from "web_myplann/Portfolio/Components/Document/MoveDocumentDialog";
import { downloadedDocumentsState } from "web_myplann/Portfolio/State/downloadedDocumentsAtom";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";
import { MyPlannInput } from "web_myplann/Shared/Components/MyPlannInput";
import { device } from "web_myplann/Utils/device";

type Props = {
	item: DocumentDTO;
	onClose: () => void;
	onOverlayOpenChange: (isOpen: boolean) => void;
	activeTag?: string;
	isDownloaded?: boolean;
};

const Actions = {
	download: "download",
	removeDownload: "removeDownload",
	rename: "rename",
	share: "share",
	move: "move",
	delete: "delete",
} as const;

export const DocumentActionsDrawer = ({
	item,
	onClose,
	onOverlayOpenChange,
	activeTag,
	isDownloaded,
}: Props) => {
	const theme = useTheme();
	const { t } = useResource();
	const queryClient = useQueryClient();

	const setDownloadedDocuments = useSetAtom(downloadedDocumentsState);

	const { mutate: renameDocument } = useDocumentEditMutation();
	const { mutate: deleteDocument } = useDocumentDeleteMutation();
	const { mutateAsync: saveDocument, isPending: isSaving } =
		useSaveDocumentMutation();
	const { mutateAsync: shareDocument, isPending: isSharing } =
		useShareDocumentMutation();

	const isProcessing = isSaving || isSharing;

	const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
	const [renameValue, setRenameValue] = useState(item.title);
	const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

	const handleAction = async (action: keyof typeof Actions) => {
		switch (action) {
			case Actions.download: {
				if (isProcessing) {
					break;
				}
				try {
					const downloaded = await saveDocument({
						id: item.id,
						fallbackTitle: item.title,
					});
					setDownloadedDocuments({ documents: downloaded });
				} catch {
					// Error toast is handled globally by MutationCache.onError.
					break;
				}
				onClose();
				break;
			}
			case Actions.share: {
				if (isProcessing) {
					break;
				}
				try {
					await shareDocument({ id: item.id, fallbackTitle: item.title });
				} catch {
					// Error toast is handled globally by MutationCache.onError.
					break;
				}
				onClose();
				break;
			}
			case Actions.rename:
				setIsRenameDialogOpen(true);
				onOverlayOpenChange(true);
				break;
			case Actions.delete:
				deleteDocument(item.id);
				onClose();
				break;
			case Actions.move:
				setIsMoveDialogOpen(true);
				onOverlayOpenChange(true);
				break;
			case Actions.removeDownload: {
				const response = await device.removeAttachment(item.filename);
				if (response.payload.success) {
					setDownloadedDocuments({ documents: response.payload.downloaded });
				} else {
					toast.error(t(Resources.Common.somethingWentWrong));
				}
				onClose();
				break;
			}
		}
	};

	const handleRename = () => {
		renameDocument({
			requestContract: {
				title: renameValue,
			},
			id: item.id,
		});
		setIsRenameDialogOpen(false);
		onClose();
		if (activeTag) {
			queryClient.invalidateQueries({
				queryKey: DocumentKeys.list(activeTag),
			});
		}
	};

	return (
		<>
			<Stack px={2} pb={2} gap={1.2}>
				{!isDownloaded ? (
					<Stack
						direction="row"
						alignItems="center"
						gap={2}
						onClick={(e) => {
							e.stopPropagation();
							handleAction(Actions.download);
						}}
					>
						{isSaving ? (
							<CircularProgress size={24} />
						) : (
							<CircleArrowDown color={theme.palette.primary.main} />
						)}
						<Typography>
							{t(
								Resources.Portfolio.List.DocumentActionsDrawer.Actions.download,
							)}
						</Typography>
					</Stack>
				) : (
					<Stack
						direction="row"
						alignItems="center"
						gap={2}
						onClick={(e) => {
							e.stopPropagation();
							handleAction(Actions.removeDownload);
						}}
					>
						<Delete color={theme.palette.primary.main} />
						<Typography>
							{t(
								Resources.Portfolio.List.DocumentActionsDrawer.Actions
									.removeDownload,
							)}
						</Typography>
					</Stack>
				)}
				{item.type === DocumentLocalTypeEnum.Local && (
					<Stack
						direction="row"
						alignItems="center"
						gap={2}
						onClick={(e) => {
							e.stopPropagation();
							handleAction(Actions.rename);
						}}
					>
						<SquarePen color={theme.palette.primary.main} />
						<Typography>
							{t(Resources.Portfolio.List.DocumentActionsDrawer.Actions.rename)}
						</Typography>
					</Stack>
				)}
				<Stack
					direction="row"
					alignItems="center"
					gap={2}
					onClick={(e) => {
						e.stopPropagation();
						handleAction(Actions.share);
					}}
				>
					{isSharing ? (
						<CircularProgress size={24} />
					) : (
						<Share2 color={theme.palette.primary.main} />
					)}
					<Typography>
						{t(Resources.Portfolio.List.DocumentActionsDrawer.Actions.share)}
					</Typography>
				</Stack>
				<Stack
					direction="row"
					alignItems="center"
					gap={2}
					onClick={(e) => {
						e.stopPropagation();
						handleAction(Actions.move);
					}}
				>
					<Archive color={theme.palette.primary.main} />
					<Typography>
						{t(Resources.Portfolio.List.DocumentActionsDrawer.Actions.move)}
					</Typography>
				</Stack>
				{item.type === DocumentLocalTypeEnum.Local && (
					<Stack
						direction="row"
						alignItems="center"
						gap={2}
						onClick={(e) => {
							e.stopPropagation();
							setIsDeleteDialogOpen(true);
						}}
					>
						<Trash2 color={theme.palette.primary.main} />
						<Typography>
							{t(Resources.Portfolio.List.DocumentActionsDrawer.Actions.delete)}
						</Typography>
					</Stack>
				)}
			</Stack>

			<MyPlannDrawer
				grabber={false}
				open={isRenameDialogOpen}
				onClose={() => {
					setIsRenameDialogOpen(false);
					onOverlayOpenChange(false);
				}}
				title={t(Resources.Portfolio.List.DocumentActionsDrawer.Actions.rename)}
			>
				<Stack px={2} pb={2} gap={1.2}>
					<MyPlannInput
						label={t(
							Resources.Portfolio.List.DocumentActionsDrawer.Rename.fileRename,
						)}
						value={renameValue}
						onChange={(e) => setRenameValue(e.target.value)}
					/>
					<Button onClick={handleRename}>{t(Resources.Common.save)}</Button>
				</Stack>
			</MyPlannDrawer>
			<MyPlannDrawer
				grabber={false}
				open={isMoveDialogOpen}
				onClose={() => {
					setIsMoveDialogOpen(false);
					onOverlayOpenChange(false);
				}}
				title={t(
					Resources.Portfolio.List.DocumentActionsDrawer.fileDrawerTitle,
				)}
			>
				<MoveDocumentDialog
					item={item}
					activeTag={activeTag}
					onClose={() => {
						setIsMoveDialogOpen(false);
						onClose();
					}}
				/>
			</MyPlannDrawer>
			<DeleteDocumentDialog
				document={item}
				open={isDeleteDialogOpen}
				onClose={(result) => {
					setIsDeleteDialogOpen(false);
					if (result) {
						onClose();
						handleAction(Actions.delete);
					}
				}}
			/>
		</>
	);
};
